import type { CSSProperties } from 'react';
import { useNavigation, useTranslate } from '@refinedev/core';
import { Link } from 'react-router-dom';
import { Alert, Flex } from 'antd';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import type { PublisherStandingResult } from '@/hooks/tenancy/usePublisherStanding';

interface PublisherEnableHintProps {
  result: PublisherStandingResult;
  style?: CSSProperties;
}

/**
 * Says up front that enabling would be refused, and why — the same reasons
 * the backend gives (`no_organization`, `no_contract`). Silent while loading,
 * on error, or when nothing blocks; the caller decides when an enable is in
 * question (show: not enabled yet, edit: "enabled" picked).
 */
export const PublisherEnableHint = ({
  result: { standing, organizationName, isLoading, isError },
  style,
}: PublisherEnableHintProps) => {
  const t = useTranslate();
  const { listUrl } = useNavigation();
  if (isLoading || isError || standing.violations.length === 0) return null;

  const k = (key: string) => t(`user_catalog.publishers.${key}`);
  const current = standing.current;

  return (
    <Alert
      type="warning"
      showIcon
      style={style}
      message={k('enable_hint.title')}
      description={
        <Flex vertical>
          {standing.violations.map((violation) => (
            <span key={violation}>{k(`standing.violations.${violation}`)}</span>
          ))}
          <span>
            {k('enable_hint.action')}{' '}
            {current ? (
              <ShowIconLink
                resource="tenancy.organizations"
                id={current.organization_id}
              >
                {organizationName(current.organization_id) ??
                  `#${current.organization_id}`}
              </ShowIconLink>
            ) : (
              <Link to={listUrl('tenancy.organizations')}>
                {t('tenancy.organizations.titles.list')}
              </Link>
            )}
          </span>
        </Flex>
      }
    />
  );
};
